import {useRefState} from '@startlibs/core'
import React, { useEffect, useMemo, useState } from 'react'
import _ from 'lodash/fp'
import {enums, Uploader2} from 'uploader-frontend'
import { jwtGetFetcher, jwtPostFetcher } from '../utils/authFetch'
import {getJwt} from '../hooks/useJwt'
import {getProviderJwt, setProviderJwt} from '../hooks/useProviderJwt'
import {useNotification} from '../components/Notifications'
import { getUploaderJwt } from '../hooks/useUploaderJwt'
import { getStorageHost } from '../hooks/useStorageHost';
import { deleteMedicalRecords, downloadFiles, summarizeMedicalRecordsDeleteResponse, viewFileSrc } from './utils';
import { ADMIN, PATIENT } from '../enums/UserRoles';
import { DISK } from '../enums/UploaderManagement';
import { Loading } from '@startlibs/components';

const apiEndpoints = {
  dicomViewer:(studies) => Promise.resolve({viewerURL:getStorageHost()+`/view/records/${studies.filter(_.get('recordUID')).map(_.get("recordUID")).join("|")}?t=${getUploaderJwt()}`}),
  nonDicomViewer: (record) => getStorageHost()+`/view/record/${record.recordUID}?t=${getUploaderJwt()}`,
  shortTokenUrl: (requestId) => jwtGetFetcher(getJwt())(`/api/shortDownloaderToken`,{requestId: requestId}),
  downloadRecord: (record) => getStorageHost()+`/download/record/${record.recordUID}?t=${getUploaderJwt()}`,
  storageHost: () => getStorageHost(),
  loadDownloadRecord: (record,filename) => record.nonCompliantFiles
  ? jwtGetFetcher(getUploaderJwt())(getStorageHost()+`/download/async/nonDicoms/${record.nonCompliantFiles.map(_.get("recordUID")).join("-")}`).then(({fileUID})=> ({url:() => getStorageHost()+`/download/file/${fileUID}?t=${getUploaderJwt()}${filename ? '&originalFilename='+encodeURIComponent(filename) : ""}`}))
  : jwtGetFetcher(getUploaderJwt())(getStorageHost()+`/download/async/${record.recordUID}`).then(({fileUID})=> ({url:() => getStorageHost()+`/download/file/${fileUID}?t=${getUploaderJwt()}${filename ? '&originalFilename='+encodeURIComponent(filename) : ""}`}))
  ,
  downloadFiles: (shortJwt, records) => {downloadFiles(shortJwt, records)},
  viewFileSrc: (shortJwt, recordId) => viewFileSrc(shortJwt, recordId),
  
}

const fileFields = ['fileName','fileExtension','fileType','fileSize','uid','description','docType','locationFormId','uploadDate']

export const RecordsManagerPatient = ({caseRequest = {}, autoGrouping, uploaderRef, listMode, caseId, allowReorder, disabled, setMedicalRecords, setCaseRequest, patientInfo, canOpenWorklist, isPatient, role, mode, setIsUploading, setToBeUploaded, setTotal, total, canAddToCase = true, ...rest}) => {
  
  const [, setNotification] = useNotification()

  const medicalRecordsStore = useRefState(() => (caseRequest.medicalRecords||[]).map((file) => ({...file,...file.info})), (prev,current) => setMedicalRecords(current))
  var studyUIDList = []
  caseRequest?.medicalRecords?.map((item) => item?.info?.studyUID ? studyUIDList.push(item?.info?.studyUID) :  null)
  const [linkedStudies, setLinkedStudiesb] = useState(studyUIDList);
  const [uploaderDiskJwt, setUploaderDiskJwt] = useState(getUploaderJwt())
  
  useEffect(() => {
    
    let refreshTimeout = -1
    if(canOpenWorklist && !isPatient){
      //let url = 'https://purviewimage.vivacloud.io/worklist/api/authenticate';
      jwtGetFetcher(getJwt())('/api/worklistToken/').then((response) => {
        setProviderJwt(response.jwt);
        refreshTimeout = setInterval(() => {
          jwtGetFetcher(getJwt())('/api/worklistToken/')
            .then((response) => {
              setProviderJwt(response.jwt)})
        },1*60*1000)

      })
    }

    if(mode===DISK && caseRequest.requestId){
      // To separate Unidentified Files between Device and Disk
      jwtGetFetcher(getJwt())(`/api/storageToken`,{requestId: caseRequest.requestId})
        .then((response) => {
          let responseJson = JSON.parse(response.jwt);
          setUploaderDiskJwt(responseJson.jwt)
        })
    }
    return  () => {
      clearInterval(refreshTimeout)
      setCaseRequest(_.unset("medicalRecords"));
    }
    
  }, [])

  const setLinkedStudies = (lStudies,type,newStudy) =>{
    
    setLinkedStudiesb(lStudies);
    
    if(type == 'Add'){
      medicalRecordsStore.set(medicalRecordsStore.get().concat(newStudy));
      setMedicalRecords(medicalRecordsStore.get());
    }
  }

  const persistRecord = (record) => {
    medicalRecordsStore.set(_.unionBy(_.property('recordUID'), [record]))
    return Promise.resolve()
  }

  const syncCountersAfterRemoval = (removedCount) => {
    if (!removedCount || !setTotal) {
      return
    }
    const nextTotal = Math.max((Number(total) || 0) - removedCount, 0)
    setTotal(nextTotal)
    if (setToBeUploaded) {
      setToBeUploaded((current) => Math.min(Number(current) || 0, nextTotal))
    }
  }

 const persistGroups = useMemo(() => {
    let lastUpdateGroups = Promise.resolve()
    let isUpdatingGroups = false
    let nextUpdateGroups = null

    const syncUpdateGroup = () => {
      if (isUpdatingGroups) {
        return lastUpdateGroups
      } else {
        const next = () => {
          isUpdatingGroups = true
          const currentGroups = nextUpdateGroups
          const updateGroups = (groups) => jwtPostFetcher(getUploaderJwt())(getStorageHost()+"/groups/case/"+caseId,{info:groups.filter(({transient})=>!transient)},{method:"PUT"}).then((_a) => {
            // setMedicalRecords(_.set('groups.list',groups));
            setCaseRequest(_.set('group.info', groups));
          })
          return updateGroups(nextUpdateGroups)
            .catch(e => {
              isUpdatingGroups = false
              return Promise.reject(e)
            })
            .then(() => {
              isUpdatingGroups = false
              if (currentGroups !== nextUpdateGroups) {
                syncUpdateGroup()
              }
            })

        }
        lastUpdateGroups = next()
        return lastUpdateGroups
      }
    }
    return (groups) => {
      nextUpdateGroups = groups
      return syncUpdateGroup()
    }
  },[])

  const persistRecordInfo = (updatedRecord,currentInfo) => {
    
    const notesPromise = updatedRecord?.notes?.note !== currentInfo?.notes?.note
      ? jwtPostFetcher(getUploaderJwt())(getStorageHost()+`/record/${updatedRecord.recordUID}/notes`,{note:updatedRecord?.notes?.note}, {method: 'PUT'})
      : Promise.resolve()
    const infoPromise = (!currentInfo || currentInfo.docType !== updatedRecord.docType || currentInfo.description !== updatedRecord.description)
      ? jwtPostFetcher(getUploaderJwt())(getStorageHost()+"/medicalRecord/info/"+updatedRecord.recordUID,{..._.pick(fileFields,updatedRecord),type:'NonDicomFile'},{method:"PUT"})
      : Promise.resolve()

    const metaPromise = !_.isEqual(updatedRecord?.metadata,currentInfo?.metadata)
      ? jwtPostFetcher(getUploaderJwt())(getStorageHost()+`/record/${updatedRecord.recordUID}/metadata`,updatedRecord?.metadata, {method: 'PUT'})
      : Promise.resolve()

    const statePromise = (updatedRecord?.state !== currentInfo?.state && currentInfo?.state)
      ? jwtPostFetcher(getUploaderJwt())(getStorageHost()+`/record/${updatedRecord.recordUID}/state`,updatedRecord?.state, {method: 'PUT'})
      : Promise.resolve()

    return Promise.all([notesPromise,infoPromise,metaPromise,statePromise])
      .then(() =>
        medicalRecordsStore.set(_.unionBy(_.property('recordUID'), [updatedRecord]))
      )
  }

  const applyDeleteResponse = (response) => {
    const {removedIds, failures, notification} = summarizeMedicalRecordsDeleteResponse(medicalRecordsStore.get(), response)

    medicalRecordsStore.set(_.reject((record) => removedIds.includes(String(record.recordUID)), medicalRecordsStore.get()))
    if (notification) {
      setNotification(notification)
    }

    syncCountersAfterRemoval(removedIds.length)
    return {removedIds, failures}
  }

  const removePersistedRecord = (recordUID) => {
    const currentRecords = medicalRecordsStore.get()

    const recordIds = recordUID === enums.MetaRecordType.ALL_NON_COMPLIANT_DICOM
      ? currentRecords.filter(record => record.format === enums.RecordFormat.NonCompliantDicom).map(_.get('recordUID'))
      : currentRecords.find(record => record.recordUID === recordUID)
        ? [recordUID]
        : []

    if (!recordIds.length) {
      return Promise.resolve(false)
    }

    return deleteMedicalRecords(caseRequest.requestId, recordIds).then(applyDeleteResponse)
  }

  const removeSelectedRecords = (selectedRecords) => {
    if (!selectedRecords.length) {
      return Promise.resolve({removedIds: [], failures: []})
    }

    return deleteMedicalRecords(caseRequest.requestId, selectedRecords).then(applyDeleteResponse)
  }

  if(mode===DISK && uploaderDiskJwt === getUploaderJwt()) {
    return <Loading style={{position: 'relative', margin: 'auto', marginTop: '1.5rem'}}
      size="24"
      borderWidth="4"
    />
  }

  return <Uploader2
      listMode={listMode}
      downloadEnabled={caseRequest}
      disabled={disabled}
      jwt={mode === DISK ? uploaderDiskJwt : getUploaderJwt}
      apiEndpoints={apiEndpoints}
      ref={uploaderRef}
      groups={caseRequest.group?.info}
      appJwt={mode === DISK ? uploaderDiskJwt : getUploaderJwt}
      persistRecord={persistRecord}
      persistRecordInfo={persistRecordInfo}
      removePersistedRecord={removePersistedRecord}
      removeSelectedRecords={removeSelectedRecords}
      persistGroups={persistGroups}
      setNotification={setNotification}
      medicalRecords={medicalRecordsStore.get()}
      role={isPatient ? PATIENT : role ? role : ADMIN}
      autoGrouping={autoGrouping}
      allowReorder={role === ADMIN ? true : false} 
      canMoveGroup={role === ADMIN ? true : false} 
      canAddGroup={role === ADMIN ? true : false} 
      // Those are to open Radiology Worklist
      patientName={patientInfo?.firstName+' '+patientInfo?.lastName}
      linkedStudies={linkedStudies}
      setLinkedStudies={setLinkedStudies}
      canOpenWorklist={canOpenWorklist}
      worklistViewerJwt={getJwt}
      providerJwt={getProviderJwt}
      withViewAllButton
      downloadFiles={downloadFiles}
      mode={mode}
      setIsUploading={setIsUploading}
      setToBeUploaded={setToBeUploaded}
      setTotal={setTotal}
      total={total}
      requestId={caseRequest.requestId}
      caseId={caseId}
      canAddToCase={canAddToCase}
    />
}